// F8 (her queue) — **PUSH DELIVERY ANALYTICS: THE DAILY GUIDANCE SENDS, PER DAY.**
//
// Her words: *"daily guidance sends per day — attempted, delivered, failed, the success rate."*
//
// **IT IS HOOKLESS AND TAKES ITS DATA AS A PROP, SO A GATE CALLS IT** (R7/R12's entry requirement,
// and `OwnerTraffic`/`OwnerLadder`'s own shape on this same tab). The analytics tab reads ONE
// payload for the whole tab; a fetch here would be a second answer to *what period is this* on a
// dashboard whose whole D6-3 ruling is one range control.
//
// **EVERY NUMBER AND EVERY SENTENCE IS THE SHARED LAW'S.** `pushDeliveryTotals` sums the period and
// `deliveryRate` decides whether a share may be drawn at all; nothing is re-derived here, because a
// surface that does its own arithmetic is a second answer key (R28/R29) — and on this table the
// second answer would be the one she reads the morning rail by.
//
// >>> WHAT THIS DOES NOT CLAIM, NAMED RATHER THAN IMPLIED (R17/R160). <<< Her clause has a second
// half — *the same per owner broadcast, each broadcast's delivery report attached to its ledger
// row* — and it is **NOT here**, because it is not buildable on today's data: every broadcast ever
// sent carries the literal tag `house:`, so under DT-6's `(tag, member)` key a second broadcast
// collides onto the first member's row and both writers keep the OLDER value — it would report as
// delivered before it was sent (DECISIONS F8-2). The repair turns on whether her broadcasts should
// STACK or REPLACE on a member's lock screen, which is a member-visible ruling and hers (F8-3). The
// heading says DAILY GUIDANCE for that reason: a table titled *push delivery* would read as
// covering a half that is missing.
import { pushDeliveryTotals, pushWaitingNote, pushUnreadableNote } from "../pushDelivery.js";

export type PushDeliveryPayload = {
  kind: string;
  graceMinutes: number;
  days: Array<{ day: string; attempted: number; delivered: number; failed: number; waiting: number; rate: number | null }>;
  unreadable: number;
};

const short = (n: number) => new Intl.NumberFormat("en-US").format(n);

export function OwnerPushDelivery({
  delivery, rangeLabel,
}: {
  delivery: PushDeliveryPayload | null | undefined; rangeLabel: string;
}) {
  // >>> THREE STATES, NEVER TWO (R18/R65), AND THE MIDDLE ONE IS THE ONE THAT COSTS SOMETHING. <<<
  // `undefined` is a payload still in flight and HOLDS STILL. `null` is a read that ARRIVED AND
  // COULD NOT BE MADE, and it says so — drawing it as an empty table puts *no morning reached
  // anybody* on the screen she judges the rail by, on a day the database refused a query. An empty
  // table with zero rows is an ANSWER and is allowed to draw as one.
  if (delivery === undefined) return null;
  if (delivery === null) {
    return (
      <section className="owner-delivery" data-delivery-state="unread">
        <h3 className="owner-detail__sub">Daily guidance delivery</h3>
        <p className="owner-page__note">These numbers could not be read just now.</p>
      </section>
    );
  }

  const days = delivery.days || [];
  const totals = pushDeliveryTotals(days);
  const waitingNote = pushWaitingNote(totals.waiting, delivery.graceMinutes);
  const missingNote = pushUnreadableNote(delivery.unreadable);

  return (
    <section className="owner-delivery" data-delivery-state="read">
      <h3 className="owner-detail__sub">Daily guidance delivery</h3>

      {/* THE PERIOD'S OWN FOUR NUMBERS, ABOVE THE ROWS THEY ARE THE SUM OF, so the headline and the
          table can never be two readings of one window (R28/R29 — they come off the same law). */}
      <div className="owner-delivery__tiles">
        <div className="owner-delivery__tile" data-delivery-tile="attempted">
          <span className="owner-delivery__n">{short(totals.attempted)}</span>
          <span className="owner-delivery__k">Sent</span>
        </div>
        <div className="owner-delivery__tile" data-delivery-tile="delivered">
          <span className="owner-delivery__n">{short(totals.delivered)}</span>
          <span className="owner-delivery__k">Reached the phone</span>
        </div>
        <div className="owner-delivery__tile" data-delivery-tile="failed">
          <span className="owner-delivery__n">{short(totals.failed)}</span>
          <span className="owner-delivery__k">Never arrived</span>
        </div>
        <div className="owner-delivery__tile" data-delivery-tile="rate">
          {/* A RATE OF NOTHING IS NOTHING, NOT ZERO. A window where nothing has settled has no
              success rate, and 0% there reads as total failure — which is the loudest possible
              wrong answer on the one table that says whether her mornings are landing. */}
          <span className="owner-delivery__n">{totals.rate === null ? "—" : `${totals.rate}%`}</span>
          <span className="owner-delivery__k">Got through</span>
        </div>
      </div>

      {days.length === 0 ? (
        // R43's M8 on her own screen: an empty table and a broken one look identical, so the empty
        // case says which it is IN HER WORDS rather than drawing a heading over nothing (T9-2).
        <p className="owner-page__note">No morning line was sent in {rangeLabel}.</p>
      ) : (
        <table className="owner-table owner-delivery__days">
          <thead>
            <tr><th>Day</th><th>Sent</th><th>Reached the phone</th><th>Never arrived</th><th>Got through</th></tr>
          </thead>
          <tbody>
            {days.map((d) => (
              <tr key={d.day} data-delivery-day={d.day}>
                <td>{d.day}</td>
                <td>{short(d.attempted)}</td>
                <td>{short(d.delivered)}</td>
                <td>{short(d.failed)}</td>
                <td>{d.rate === null ? "—" : `${d.rate}%`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* >>> THE ONE SENTENCE THAT MAKES THE ARITHMETIC ADD UP ON TODAY'S ROW. <<< Sent minus reached
          does not equal never-arrived while a send is still inside the beacon's grace, and without
          this line that gap reads as a table that cannot count. It is EMPTY whenever nothing is in
          flight, which is every row but this morning's — a screen that keeps explaining itself after
          there is nothing to explain is one she stops reading. */}
      {waitingNote ? <p className="owner-page__note owner-delivery__note">{waitingNote}</p> : null}
      {/* AND A DAY THE LAW COULD NOT READ IS NAMED RATHER THAN DROPPED (R17/R160): a table that is
          quietly short reads as a quiet week. */}
      {missingNote ? <p className="owner-page__note owner-delivery__note">{missingNote}</p> : null}
    </section>
  );
}
